import {
  Avatar,
  Box,
  Button,
  Grid,
  Paper,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import LockResetIcon from "@mui/icons-material/LockReset";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { useState, FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import authApi from "Service/Auth.api";
import { RouteUrls } from "Constants/RouteUrls";
import { useDispatch } from "react-redux";
import {
  setAlertData,
  startLoading,
  stopLoading,
} from "../../Redux/Screen/Action";
import ImgPath from "Constants/Imgs";
import Home from "@mui/icons-material/Home";

const ForgotPassword = () => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [email, setEmail] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  const handleResetPassword = async () => {
    setError("");
    dispatch(startLoading({ loading: true, loadtext: "Reset in Progress..." }));
    if (!email.trim()) {
      setError("Email is required.");
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError("Enter a valid email address.");
      return;
    }

    setLoading(true);
    try {
      const res = await authApi.forgotPassword({ email });
      console.log("res", res);
      if (res?.success) {
        dispatch(stopLoading());
        dispatch(
          setAlertData({
            alertMessage: `${res?.alert?.alertMessage}`,
            alertVariant: "success",
            openAlert: true,
          })
        );
      } else {
        setError(res?.message || "Failed to send reset link.");
      }
    } catch (err) {
      console.error(err);
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    handleResetPassword();
  };

  return (
    <Box sx={{ height: "100vh", overflow: "auto" }}>
      <Box>
        <Typography
          variant="h4"
          sx={{
            textAlign: "center",
            mt: 1,
            mb: 1,
            color: "primary.main",
          }}
        >
          <Box
            component="img"
            src={"https://nolegalpaisa.com/assets/images/nlp_logo.png"}
            alt="Logo"
            sx={{
               width: { xs: "150px", sm: "200px", md: "100px" },
              height: { xs: "60px", sm: "80px", md: "83px" },
              objectFit: "contain",
              cursor: "pointer",
              filter: "drop-shadow(0px 4px 8px rgba(0, 0, 0, 0.3))",
              transition: "transform 0.2s ease-in-out",
              "&:hover": {
                transform: "scale(1.05)",
              },
            }}
            onClick={() => (window.location.href = "/home.html")}
          />
        </Typography>
      </Box>
      <Grid
        container
        component="main"
        sx={{ 
          height: { xs: "auto", sm: "80vh" },
          minHeight: { xs: "70vh", sm: "80vh" },
          py: { xs: 2, sm: 0 }
        }}
        alignItems="center"
        justifyContent="center"
      >
        <Grid
          size={{
            xs: 12,
            sm: 12,
            md: 8,
            lg: 6,
          }}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            px: { xs: 2, sm: 3, md: 4 },
          }}
        >
          <Paper
            elevation={6}
            square
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: { xs: "auto", sm: "100%" },
              width: "100%",
              maxWidth: { xs: "100%", sm: "450px", md: "500px" },
              minHeight: { xs: "auto", sm: "400px" },
              py: { xs: 3, sm: 2 },
            }}
          >
            <Box
              sx={{
                mx: { xs: 2, sm: 3, md: 4 },
                width: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                overflow: "visible",
              }}
            >
              <Avatar 
                sx={{ 
                  m: 1, 
                  bgcolor: "primary.main",
                  width: { xs: 40, sm: 56 },
                  height: { xs: 40, sm: 56 }
                }}
              >
                <LockResetIcon sx={{ fontSize: { xs: "1.2rem", sm: "1.5rem" } }} />
              </Avatar>
              <Typography 
                component="h1" 
                variant="h5" 
                sx={{ 
                  mb: 2,
                  fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2rem" },
                  textAlign: "center"
                }}
              >
                Forgot Password
              </Typography>

              <Typography
                variant="body2"
                sx={{ 
                  mb: 2, 
                  textAlign: "center", 
                  color: "text.secondary",
                  fontSize: { xs: "0.875rem", sm: "1rem" },
                  px: { xs: 1, sm: 0 }
                }}
              >
                Enter your registered email and we'll send you a link to reset
                your password.
              </Typography>

              <Box
                component="form"
                noValidate
                sx={{ 
                  mt: 1, 
                  width: "100%",
                  maxWidth: { xs: "100%", sm: "400px" }
                }}
                onSubmit={handleSubmit}
              >
                <TextField
                  fullWidth
                  label="Email Address"
                  variant="outlined"
                  margin="normal"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  error={!!error}
                  helperText={error}
                  sx={{
                    "& .MuiInputBase-root": {
                      fontSize: { xs: "0.875rem", sm: "1rem" }
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: { xs: "0.875rem", sm: "1rem" }
                    }
                  }}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  color="primary"
                  sx={{ 
                    mt: 3, 
                    color: "#fff", 
                    backgroundColor: "primary.main",
                    py: { xs: 1.5, sm: 1.75 },
                    fontSize: { xs: "0.875rem", sm: "1rem" }
                  }}
                  disabled={loading}
                >
                  {loading ? "Sending..." : "Send Reset Link"}
                </Button>

                <Typography
                  sx={{
                    textAlign: "center",
                    textDecoration: "underline",
                    cursor: "pointer",
                    mt: 2,
                    fontSize: { xs: "0.875rem", sm: "1rem" }
                  }}
                  variant="body2"
                  onClick={() => navigate(`/${RouteUrls.login}`)}
                >
                  Back to Login
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ForgotPassword;
